// frontend-customer/src/utils/useClosingStatus.js

import { useState, useEffect } from 'react';
import { getVendorLocalTime } from './vendorTime';

export const useClosingStatus = (scheduleData, country = 'India') => {
    const [status, setStatus] = useState({
        isClosingSoon: false,
        minutesLeft: 0,
        nextDayDeliveryTime: ''
    });

    useEffect(() => {
        if (!scheduleData) return;

        const calculateStatus = () => {
            let hoursObj = scheduleData;
            if (typeof scheduleData === 'string') {
                try { hoursObj = JSON.parse(scheduleData); } catch (e) { return; }
            }

            const vendorNow = getVendorLocalTime(country);
            const currentMinutes = vendorNow.hour * 60 + vendorNow.minute;
            const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
            const todayIdx = days.indexOf(vendorNow.weekday.toLowerCase());
            const todayStr = days[todayIdx];
            const yesterdayStr = days[(todayIdx + 6) % 7];

            const parseTime = (timeStr) => {
                if (!timeStr) return 0;
                const [time, modifier] = timeStr.split(' ');
                let [hours, minutes] = time.split(':').map(Number);
                if (modifier === 'PM' && hours !== 12) hours += 12;
                if (modifier === 'AM' && hours === 12) hours = 0;
                return hours * 60 + minutes;
            };

            const formatTime = (totalMins) => {
                let h = Math.floor(totalMins / 60);
                let m = totalMins % 60;
                const ampm = h >= 12 ? 'PM' : 'AM';
                h = h % 12;
                if (h === 0) h = 12;
                return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')} ${ampm}`;
            };

            // Legacy Fallback mapping
            if (hoursObj.open && hoursObj.close) {
                const legacyStart = hoursObj.open;
                const legacyEnd = hoursObj.close;
                hoursObj = {};
                days.forEach(d => {
                    hoursObj[d] = { isClosed: false, shifts: [{ startTime: legacyStart, endTime: legacyEnd }] };
                });
            }

            let activeShiftEndMins = null;

            // 1. Check yesterday's overnight shift
            const yesterdaySchedule = hoursObj[yesterdayStr];
            if (yesterdaySchedule && !yesterdaySchedule.isClosed) {
                for (const shift of yesterdaySchedule.shifts) {
                    const startMins = parseTime(shift.startTime);
                    const endMins = parseTime(shift.endTime);
                    if (endMins <= startMins && currentMinutes <= endMins) {
                        activeShiftEndMins = endMins;
                    }
                }
            }

            // 2. Check today's shifts
            const todaySchedule = hoursObj[todayStr];
            if (!activeShiftEndMins && todaySchedule && !todaySchedule.isClosed) {
                for (const shift of todaySchedule.shifts) {
                    const startMins = parseTime(shift.startTime);
                    const endMins = parseTime(shift.endTime);
                    if (endMins <= startMins) {
                        if (currentMinutes >= startMins || currentMinutes <= endMins) {
                            // If it ends tomorrow, add 1440 mins to make the math easy
                            activeShiftEndMins = currentMinutes >= startMins ? endMins + 1440 : endMins; 
                        }
                    } else {
                        if (currentMinutes >= startMins && currentMinutes <= endMins) {
                            activeShiftEndMins = endMins;
                        }
                    }
                }
            }

            // --- ✅ NEW: Always calculate the next available shift first ---
            let nextOpenDayOffset = 0;
            let nextShift = null;

            for (let i = 0; i < 7; i++) {
                const checkDayIdx = (todayIdx + i) % 7;
                const checkDayStr = days[checkDayIdx];
                const schedule = hoursObj[checkDayStr];

                if (schedule && !schedule.isClosed && schedule.shifts && schedule.shifts.length > 0) {
                    let validShifts = schedule.shifts.map(s => ({...s, startMins: parseTime(s.startTime)})).sort((a,b) => a.startMins - b.startMins);
                    
                    if (i === 0) {
                        validShifts = validShifts.filter(s => s.startMins > currentMinutes);
                    }
                    
                    if (validShifts.length > 0) {
                        nextShift = validShifts[0];
                        nextOpenDayOffset = i;
                        break;
                    }
                }
            }

            let nextDayDeliveryTime = '';
            if (nextShift) {
                const openTimeMins = parseTime(nextShift.startTime);
                const deliveryMins = (openTimeMins + 60) % 1440; // Estimated 60 min prep time
                let dayPrefix = nextOpenDayOffset === 0 ? "Today" : nextOpenDayOffset === 1 ? "Tomorrow" : days[(todayIdx + nextOpenDayOffset) % 7].charAt(0).toUpperCase() + days[(todayIdx + nextOpenDayOffset) % 7].slice(1);
                nextDayDeliveryTime = `${dayPrefix} at ${formatTime(deliveryMins)}`;
            }

            // --- Apply States ---
            if (activeShiftEndMins !== null) {
                // BUSINESS IS OPEN - Check if closing soon
                let diffMins = activeShiftEndMins - currentMinutes;
                if (diffMins < 0) diffMins += 1440; // Math wrapper for overnight

                if (diffMins > 0 && diffMins <= 30) {
                    // ✅ FIXED: Pass the calculated time so the banner can display it!
                    setStatus({ isClosingSoon: true, minutesLeft: diffMins, nextDayDeliveryTime });
                } else {
                    setStatus({ isClosingSoon: false, minutesLeft: 0, nextDayDeliveryTime: '' });
                }
            } else {
                // BUSINESS IS CLOSED
                setStatus({ isClosingSoon: false, minutesLeft: 0, nextDayDeliveryTime });
            }
        };

        calculateStatus();
        const timer = setInterval(calculateStatus, 60000);
        return () => clearInterval(timer);

    }, [scheduleData, country]);

    return status;
};